import { useEffect, useRef } from 'react';
import { performSilentOidcAuth } from '@diligentcorp/library-oidc-client-frontend-helpers';
import AppConfig from '../AppConfig';
import addScript from '../helpers/addScript';

async function loadScriptWithAuth(url: string, resourceIndicator: string): Promise<void> {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return;
  }

  let preflightResponse: Response;
  try {
    preflightResponse = await fetch(url, { method: 'HEAD', credentials: 'include' });
  } catch (err) {
    console.error('Monitor script preflight failed', err);
    return;
  }

  if (!preflightResponse.ok && preflightResponse.status !== 401 && preflightResponse.status !== 403) {
    console.error(`Monitor script preflight failed: ${preflightResponse.status} ${preflightResponse.statusText}`);
    return;
  }

  if (preflightResponse.status === 401 || preflightResponse.status === 403) {
    try {
      // Use performSilentOidcAuth (not WithFallbackToRedirect) to avoid redirecting
      // the user away from the page just because Pendo auth failed
      await performSilentOidcAuth(
        new URL(
          `${AppConfig.oidcAuthStartEndpointPath}?resource=${encodeURIComponent(resourceIndicator)}`,
          window.location.origin,
        ),
        resourceIndicator,
        'origin',
      );
    } catch (err) {
      console.error('Monitor script silent auth failed', err);
      return;
    }

    let retryResponse: Response;
    try {
      retryResponse = await fetch(url, { method: 'HEAD', credentials: 'include' });
    } catch (err) {
      console.error('Monitor script preflight retry failed', err);
      return;
    }

    if (!retryResponse.ok) {
      console.error(`Monitor script preflight retry failed: ${retryResponse.status} ${retryResponse.statusText}`);
      return;
    }
  }

  addScript(url);
}

export function useInitScripts(isSessionSuccess: boolean) {
  const scriptsInitialized = useRef(false);

  useEffect(() => {
    if (!isSessionSuccess || scriptsInitialized.current) return;
    scriptsInitialized.current = true;

    // Only handle sidecar mode here - legacy mode loads Pendo directly in main.tsx
    if (!AppConfig.sidecarAuthEnabled || AppConfig.isGovCloud || (window as any).pendo) return;

    loadScriptWithAuth(AppConfig.pendoMonitorProxyScript, AppConfig.proxyApiEndpoints.monitor.oidcResourceIndicator);
  }, [isSessionSuccess]);
}
